import Axios from 'axios';
import * as GradesRequest from '@/models/gradesRequest';
import * as GradesResponse from '@/models/gradesResponse';
import { Subject } from '@/models/subject';
import { GradesTypes } from '@/enums/gradesTypes';
import { SchoolTypes } from '@/enums/schoolTypes';
import { SchoolTerms } from '@/enums/schoolTerms';
import { GradeYears } from '@/enums/gradeYears';

export type Grades = GradesResponse.GradesDetailResponse;

export interface EditGrades {
  gradesId: string | null;
  gradesCollectionId: string;
  studentId: string;
  gradeYear: GradeYears;
  schoolTerm: SchoolTerms;
  pointList: GradesRequest.PointsRequest[];
}

export interface SearchCondition {
  gradesType: GradesTypes;
  schoolType: SchoolTypes;
  schoolTerm: SchoolTerms;
  gradeYear: GradeYears;
  testType: string;
  gradesCollectionId: string;
  tagIdList: number[];
  keyWord: string;
  sort: string;
}

export interface GradesData {
  loading: boolean;
  gradesList: Grades[];
  searchCondition: SearchCondition;
}

interface State {
  report: GradesData;
  routineTest: GradesData;
  otherTest: GradesData;
}
const mapState: State = {
  report: {
    loading: false,
    gradesList: new Array<Grades>(),
    searchCondition: {
      gradesType: GradesTypes.Report,
      schoolType: SchoolTypes.MiddleSchool,
      schoolTerm: SchoolTerms.All,
      gradeYear: GradeYears.All,
      testType: '',
      gradesCollectionId: '',
      tagIdList: new Array<number>(),
      keyWord: '',
      sort: '',
    }
  },
  routineTest: {
    loading: false,
    gradesList: new Array<Grades>(),
    searchCondition: {
      gradesType: GradesTypes.RoutineTest,
      schoolType: SchoolTypes.MiddleSchool,
      schoolTerm: SchoolTerms.All,
      gradeYear: GradeYears.All,
      testType: '',
      gradesCollectionId: '',
      tagIdList: new Array<number>(),
      keyWord: '',
      sort: '',
    }
  },
  otherTest: {
    loading: false,
    gradesList: new Array<Grades>(),
    searchCondition: {
      gradesType: GradesTypes.OtherTest,
      schoolType: SchoolTypes.MiddleSchool,
      schoolTerm: SchoolTerms.All,
      gradeYear: GradeYears.All,
      testType: '',
      gradesCollectionId: '',
      tagIdList: new Array<number>(),
      keyWord: '',
      sort: '',
    }
  },
};

const mapActions = {
  async searchGradesList({ commit }: any, searchCondition: SearchCondition) {
    let searchNG = false;
    const gradesType = searchCondition.gradesType;
    let request: GradesRequest.GradesRequest | null = null;
    switch (gradesType) {
      case GradesTypes.Report:
      case GradesTypes.RoutineTest:
        if (searchCondition.schoolType === SchoolTypes.All) {
          searchNG = true;
        }
        if (searchCondition.schoolTerm === SchoolTerms.All) {
          searchNG = true;
        }
        request = {
          gradesId: undefined,
          studentId: undefined,
          gradesCollectionId: undefined,
          gradesType,
          schoolType: searchCondition.schoolType,
          schoolTerm: searchCondition.schoolTerm,
          gradeYear: searchCondition.gradeYear,
          testType: undefined,
          fiscalYear: undefined,
          testTimes: undefined,
          tagIdList: searchCondition.tagIdList,
          keyWord: searchCondition.keyWord,
          sort: searchCondition.sort,
          pointList: undefined,
        };
        break;
      case GradesTypes.OtherTest:
        if (searchCondition.schoolType === SchoolTypes.All) {
          searchNG = true;
        }
        if (!searchCondition.testType || searchCondition.testType === '') {
          searchNG = true;
        }
        if (!searchCondition.gradesCollectionId || searchCondition.gradesCollectionId === '') {
          searchNG = true;
        }
        request = {
          gradesId: undefined,
          studentId: undefined,
          gradesCollectionId: searchCondition.gradesCollectionId,
          gradesType,
          schoolType: searchCondition.schoolType,
          schoolTerm: undefined,
          gradeYear: searchCondition.gradeYear,
          testType: searchCondition.testType,
          fiscalYear: undefined,
          testTimes: undefined,
          tagIdList: searchCondition.tagIdList,
          keyWord: searchCondition.keyWord,
          sort: searchCondition.sort,
          pointList: undefined,
        };
        break;
    }

    if (searchNG || !request) {
      commit('clearGradesList', gradesType);
      return;
    }

    commit('setLoading', {
      gradesType,
      loading: true,
    });

    const response = await Axios.post('/api/Grades/Search', request).catch((error) => {
      commit('setLoading', {
        gradesType,
        loading: false,
      });
      throw error;
    });
    response.data.gradesList.forEach((grades: Grades) => {
      grades.pointsList.forEach((item) => {
        item.beforePoints = item.points;
      });
    });
    commit('fetchGradesList', {
      gradesType,
      data: response.data,
    });
  },
  async editGrades({ commit }: any, editGrades: EditGrades) {
    const request: GradesRequest.GradesRequest = {
      gradesId: !editGrades.gradesId ? undefined : editGrades.gradesId,
      studentId: editGrades.studentId,
      gradesCollectionId: editGrades.gradesCollectionId,
      gradeYear: editGrades.gradeYear,
      schoolTerm: editGrades.schoolTerm,
      gradesType: undefined,
      schoolType: undefined,
      testType: undefined,
      fiscalYear: undefined,
      testTimes: undefined,
      tagIdList: undefined,
      keyWord: undefined,
      sort: undefined,
      pointList: editGrades.pointList.map((item) => ({
        subject: item.subject,
        points: item.points,
      })),
    };
    const response = await Axios.post('/api/Grades/Create', request).catch((error) => {
      throw error;
    });
  },
};

const mapMutations = {
  setLoading(
    state: State,
    { gradesType, loading }: { gradesType: GradesTypes, loading: boolean }
  ) {
    let target: GradesData;
    switch (gradesType) {
      case GradesTypes.Report:
        target = state.report;
        break;
      case GradesTypes.RoutineTest:
        target = state.routineTest;
        break;
      case GradesTypes.OtherTest:
        target = state.otherTest;
        break;
      default:
        return;
    }
    target.loading = loading;
  },
  fetchGradesList(
    state: State,
    { gradesType, data }: { gradesType: GradesTypes, data: GradesResponse.GradesListResponse }
  ) {
    let target: GradesData;
    switch (gradesType) {
      case GradesTypes.Report:
        target = state.report;
        break;
      case GradesTypes.RoutineTest:
        target = state.routineTest;
        break;
      case GradesTypes.OtherTest:
        target = state.otherTest;
        break;
      default:
        return;
    }
    target.gradesList = data.gradesList;
    target.loading = false;
  },
  clearGradesList(state: State, gradesType: GradesTypes) {
    let target: GradesData;
    switch (gradesType) {
      case GradesTypes.Report:
        target = state.report;
        break;
      case GradesTypes.RoutineTest:
        target = state.routineTest;
        break;
      case GradesTypes.OtherTest:
        target = state.otherTest;
        break;
      default:
        return;
    }
    target.gradesList = [];
    target.loading = false;
  },
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
